/**
 * Human-readable copy for auth error codes.
 *
 * WHY: the shop used to render backend error codes straight at the customer —
 * `toast.error(t(errorCode))`. `customer_require_verification` exists in NONE
 * of the 12 locale files, so Tolgee fell through to the key itself and a stuck
 * customer was shown the literal string "customer_require_verification"
 * (OMS-185 / W1).
 *
 * Adding the raw codes to i18n/*.json would not hold: those files are pulled
 * from Tolgee (`pnpm i18n:pull`) and would be overwritten. So instead we map
 * codes to English SENTENCES and use those as the translation keys — the
 * established convention in this app, where keys are the English source text.
 * A sentence key renders correctly even before it is added in Tolgee, then
 * picks up real translations once it is.
 *
 * Note for translators: the sentences below still need adding to Tolgee to be
 * localised for bm_MY / zh_MY / zh_TW / vi_VN etc. Until then they render in
 * English — which is strictly better than a snake_case code.
 */

export const GENERIC_AUTH_ERROR_MESSAGE =
  'We could not complete your sign in. Please try again, or contact the store if this keeps happening.'

export const VERIFICATION_LOOP_MESSAGE =
  'We could not verify your account. Please contact the store to complete your order.'

export const SIGN_IN_UNAVAILABLE_MESSAGE =
  'Sign in is unavailable right now. Please refresh the page and try again.'

/**
 * Shown instead of opening Oddle Pass when the browser blocks cookies. Says
 * what to change and where, because "sign in failed" would send the customer
 * round the same loop — there is no web API that can turn cookies back on for
 * them, so the only way out is the browser's own settings.
 */
export const COOKIES_BLOCKED_MESSAGE =
  'Your browser is blocking cookies, so we cannot sign you in. Please allow cookies for this site in your browser settings, then try again.'

/**
 * Shown on the menu itself, before the customer touches anything. Ordering — not
 * just sign-in — is impossible with cookies blocked: the cart cannot be held, so
 * every Add would fail. Says what to change and where, because there is no web
 * API that can re-enable cookies on the customer's behalf.
 */
export const STORAGE_BLOCKED_ORDERING_MESSAGE =
  'Your browser is blocking cookies for this site, so orders cannot be placed. Please allow cookies in your browser settings and reload the page.'

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  customer_require_verification:
    'Please verify your account to continue with your order.',
  request_new_access_token:
    'Your session has expired. Please sign in again to continue.',
  customer_not_found: 'We could not find your account. Please sign in again.',
  invalid_access_token:
    'Your session is no longer valid. Please sign in again to continue.',
}

/**
 * Machine codes are snake_case/kebab-case with no spaces. Anything matching
 * that shape must never reach a customer's screen, even when we have no
 * mapping for it — an unmapped code is a bug on our side, not a message.
 */
const looksLikeMachineCode = (value: string): boolean =>
  !value.includes(' ') && /[_-]/.test(value)

/**
 * @param errorCode raw code from the OMS / customer-info response
 * @param translate the `t` from useTranslate()
 */
export const getAuthErrorMessage = (
  errorCode: string | undefined | null,
  translate: (key: string) => string,
): string => {
  if (!errorCode) return translate(GENERIC_AUTH_ERROR_MESSAGE)

  const mapped = AUTH_ERROR_MESSAGES[errorCode]
  if (mapped) return translate(mapped)

  if (looksLikeMachineCode(errorCode))
    return translate(GENERIC_AUTH_ERROR_MESSAGE)

  // Already a human sentence (some OMS errors are), so pass it through.
  return translate(errorCode)
}
